import { map, finalize, from, catchError, of, switchMap } from "rxjs";
import http from "../../services/httpClient";
import pad from "./helpers/pad";

const { get$ } = http;

const getApiClient = () => {
  const isLocal = window.location.origin === "http://local.varsome.com:3000";
  const localProxy = "http://localhost:8011/proxy";
  const clinicalPath = isLocal ? localProxy : window.location.origin;
  const activeDict = new Map();

  function getSharedRequest(url, params, processData) {
    if (!activeDict.get(url)) {
      const req = get$(url, params).pipe(map(processData));
      activeDict.set(url, req);
    }

    const req = activeDict.get(url);
    return from(req).pipe(finalize(() => activeDict.delete(url)));
  }

  function getRefSamples({ originAnalId }) {
    const path = `api/v1/analysis/exome-depth-data/qc-samples/${originAnalId}`;
    return get$(path, { url: clinicalPath });
  }

  function loadGenes({ chr, analysisId }) {
    const params = { "genome-id": chr.genomeId, chr: chr.shortName };
    const path = `api/v1/analysis/chromosome-bands/${analysisId}`;
    return get$(path, { url: clinicalPath, params });
  }

  function loadReads({ chr, originAnalId, sampleCsv }) {
    const params = {
      chr: chr.shortName,
      "graph-sample-id": sampleCsv,
    };
    const path = `api/v1/analysis/exome-depth-data/graph-samples/${originAnalId}`;
    return get$(path, { url: clinicalPath, params });
  }

  function loadCoverage({ chr, originAnalId }) {
    const params = { chr: chr.shortName };
    const path = `api/v1/analysis/exome-depth-data/coverage/${originAnalId}`;
    return get$(path, { url: clinicalPath, params });
  }

  function getChrPhasing({ analysisId, chrId }) {
    const conf = { url: clinicalPath };
    const path = `api/v1/phasing_groups/${analysisId}/${chrId}`;
    return getSharedRequest(path, conf, (x) => x);
  }

  function getAnalysisVariants(processData) {
    return ({
      analysisId,
      x0,
      x1,
      chr,
      loadedPhasingGroupChrSet: loadedPhaseChr,
      hasPhasingGroups: hasPhasing,
    }) => {
      const start = Math.max(x0, 1);
      const end = Math.min(chr.region.x1, x1);
      const range = end - start;
      const genome = chr.genomeId === "hg38" ? 1038 : 1019;
      const { id: chrId } = chr;
      const pos = pad(start, 9, "0");
      const posId = `${genome}${pad(chrId, 3, "0")}${pos}0000`;
      const path = `api/v1/variants-in-analysis/${analysisId}/${posId}/${range}`;
      const conf = { url: clinicalPath };
      const loadPhase = hasPhasing && !loadedPhaseChr.has(chr.id);
      const phaseConf = { analysisId, chrId };

      return of(null).pipe(
        switchMap(() => (loadPhase ? getChrPhasing(phaseConf) : of(null))),
        switchMap((x) => {
          return getSharedRequest(path, conf, processData).pipe(
            map((y) => ({ sampleVariants: y, phasing: x }))
          );
        })
      );
    };
  }

  function getAnalysisCnvs(processData) {
    return ({ analysisId, x0, x1, chr }) => {
      const start = Math.max(x0, 1);
      const end = Math.min(chr.region.x1, x1);
      const range = end - start;
      const genome = chr.genomeId === "hg38" ? 1038 : 1019;
      const chrId = pad(chr.id, 3, "0");
      const pos = pad(start, 9, "0");
      const posId = `${genome}${chrId}${pos}0000`;
      const url = `api/v1/cnvs-in-analysis/${analysisId}/${posId}/${range}`;
      const params = { all_cnv_analyses: 1 }; // min_length: 1, max_length: 200000000,
      const conf = { url: clinicalPath, params };
      return getSharedRequest(url, conf, processData);
    };
  }

  function searchGenes({ genomeId, query }) {
    const url = `genes/search/${genomeId}/`;
    const params = { query };
    return get$(url, { params });
  }

  function loadAnalysisSamples(analysisId) {
    const url = `api/v1/sample-cnv/${analysisId}`;
    return get$(url, { url: clinicalPath });
  }

  function loadAnalysisInfo(analysisId) {
    const url = `api/v1/analysis/${analysisId}`;
    return get$(url, { url: clinicalPath });
  }

  function loadTranscriptInfo({ genomeId, selectedTranscript }) {
    const url = `lookup/transcript/${selectedTranscript}/${genomeId}`;
    return get$(url);
  }

  function loadSequence({ chrId, genomeId, x0, x1 }) {
    const url = `sequence/${genomeId}/${chrId}/${x0}/${x1 - x0}`;
    return get$(url);
  }

  function loadTranscripts({ genomeId, chrId, x0, x1 }) {
    const range = x1 - x0;
    const url = `overlapping_transcripts/${genomeId}/${chrId}/${x0}/${range}`;
    return get$(url).pipe(map((x) => x.features));
  }

  function loadPathogenicity({ genomeId, chrId, x0, x1 }) {
    const genome = genomeId === "hg38" ? 1038 : 1019;
    const range = x1 - x0;
    const url = `region_pathogenicity/${genome}/${chrId}/${x0}/${range}`;
    return get$(url, url);
  }

  function getNearbyVariants({ genomeId, chrId, x0, x1 }) {
    const range = x1 - x0;
    const params = { max_length: 100 };
    const url = `region_variants/${genomeId}/${chrId}/${x0}/${range}`;
    return get$(url, { params });
  }

  function loadProteinRegions({ genomeId, chrId, x0, x1 }) {
    const genome = genomeId === "hg38" ? 1038 : 1019;
    const range = x1 - x0;
    const url = `region_databases_data/${genome}/${chrId}/${x0}/${range}`;
    return get$(url);
  }

  function loadConservation({ genomeId, chrId, x0, x1 }) {
    const genome = genomeId === "hg38" ? 1038 : 1019;
    const range = x1 - x0;
    const url = `conservation/${genome}/${chrId}/${x0}/${range}`;
    return get$(url);
  }

  function loadChrInfo({ genomeId, chrName }) {
    const url = `chromosome_info/${genomeId}/${chrName}`;
    return get$(url).pipe(map((x) => ({ ...x, name: chrName })));
  }

  function getRohBedFile({ sampleId }) {
    const analysisId = `${sampleId}000000`;
    const url = `api/v1/analysis/${analysisId}/roh/100kb/`;
    return getSharedRequest(url, { url: clinicalPath }, (x) => x).pipe(
      catchError(() => of(""))
    );
  }

  function loadAllTranscripts({ genomeId, chrId, x0, x1 }) {
    const range = x1 - x0;
    const params = {
      main_transcripts_only: 0,
      include_exons: 1,
      add_all_info: 1,
    };
    const url = `overlapping_transcripts/${genomeId}/${chrId}/${x0}/${range}`;
    return get$(url, { params }).pipe(map((x) => x.features));
  }

  function loadMainTranscripts({ genomeId, chrId, x0, x1 }) {
    const range = x1 - x0;
    const params = {
      main_transcripts_only: 1,
      include_exons: 1,
      add_all_info: 1,
    };
    const url = `overlapping_transcripts/${genomeId}/${chrId}/${x0}/${range}`;
    return get$(url, { params }).pipe(map((x) => x.features));
  }

  function loadDellyCnvCalls(props) {
    const { genomeId, chrId, analysisId } = props;
    const genome = genomeId === "hg38" ? 1038 : 1019;
    const pos = pad("1", 9, "0");
    const posId = `${genome}${pad(chrId, 3, "0")}${pos}0000`;
    const url = `api/v1/delly-cnv-calls/${analysisId}/${posId}`;
    return get$(url, { url: clinicalPath });
  }

  function loadDellyCoverage(props) {
    const { genomeId, chrId, analysisId, x0, size } = props;
    const genome = genomeId === "hg38" ? 1038 : 1019;
    const pos = pad(x0, 9, "0");
    const posId = `${genome}${pad(chrId, 3, "0")}${pos}0000`;
    const url = `api/v1/delly-bam-coverage/${analysisId}/${posId}/${size}`;
    return get$(url, { url: clinicalPath });
  }

  return {
    loadDellyCnvCalls,
    getRefSamples,
    loadGenes,
    loadReads,
    loadCoverage,
    loadDellyCoverage,
    loadAllTranscripts,
    getAnalysisVariants,
    getAnalysisCnvs,
    searchGenes,
    loadAnalysisSamples,
    loadAnalysisInfo,
    loadTranscriptInfo,
    loadSequence,
    loadTranscripts,
    loadMainTranscripts,
    loadPathogenicity,
    getNearbyVariants,
    loadProteinRegions,
    loadConservation,
    loadChrInfo,
    getRohBedFile,
  };
};

export default getApiClient;
